import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import LogsVazio from "../../Compartilhado/LogsVazio";
import { CampoAlterado, DadosPendenciaFinalizada } from "./types";
import { mapearPendenciasFinalizadas } from "./utils";
import BlocoPendenciasFinalizadas from "./Blocos/BlocoPendenciasFinalizadas";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const formatarNomeProfissional = async (id_usuario: number | null | undefined): Promise<string> => {
    try {
        if (!id_usuario || id_usuario === 0) {
            return "—";
        }
        
        const id = Number(id_usuario);
        if (isNaN(id) || id <= 0) {
            return "—";
        }
        
        try {
            const response = await api.get(`usuario/${id}`);
            if (response?.data?.status === 'success' && response?.data?.result?.nome) {
                return `${response.data.result.nome} (D${id})`;
            }
        } catch (error: any) {
            if (error?.response?.status && error.response.status !== 404) {
                return `D${id}`;
            }
        }
        
        return `D${id}`;
    } catch {
        const id = Number(id_usuario);
        return isNaN(id) || id <= 0 ? "—" : `D${id}`;
    }
};

const compararDados = async (dadosAntes: any, dadosDepois: any, dadosExtra: any): Promise<CampoAlterado[]> => {
    const campos: CampoAlterado[] = [];

    const idProfissionalAntes = dadosAntes?.id_usuario;
    const idProfissionalDepois = dadosDepois?.id_usuario;

    if (idProfissionalAntes !== undefined && idProfissionalDepois !== undefined && idProfissionalAntes !== idProfissionalDepois) {
        const nomeProfissionalNovo = dadosExtra?.nome_usuario;
        const profissionalAnterior = dadosExtra?.profissional_anterior;
        
        let antesTexto: string | null = null;
        if (idProfissionalAntes && idProfissionalAntes > 0) {
            if (profissionalAnterior && profissionalAnterior.nome) {
                antesTexto = `${profissionalAnterior.nome} (D${idProfissionalAntes})`;
            } else {
                antesTexto = await formatarNomeProfissional(idProfissionalAntes);
            }
        }

        let depoisTexto: string;
        if (nomeProfissionalNovo) {
            depoisTexto = `${nomeProfissionalNovo} (D${idProfissionalDepois})`;
        } else {
            depoisTexto = await formatarNomeProfissional(idProfissionalDepois);
        }

        campos.push({
            campo: 'Profissional responsável',
            antes: antesTexto,
            depois: depoisTexto
        });
    }

    return campos;
};

export default function ExpandedContentProfissionalAlterado({ 
    item, 
    onNavigateToFile 
}: ExpandedContentProps) {
    const [camposPrincipal, setCamposPrincipal] = useState<CampoAlterado[]>([]);
    const [pendenciasFinalizadas, setPendenciasFinalizadas] = useState<DadosPendenciaFinalizada[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response?.data?.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log?.entidade_nome === 'proposta');

                    if (logProposta) {
                        let dadosAntes = logProposta.dados_antes;
                        let dadosDepois = logProposta.dados_depois;
                        let dadosExtra = logProposta.dados_extra;

                        dadosAntes = parseJsonSafely(dadosAntes);
                        dadosDepois = parseJsonSafely(dadosDepois);
                        dadosExtra = parseJsonSafely(dadosExtra);

                        const campos = await compararDados(dadosAntes, dadosDepois, dadosExtra);
                        setCamposPrincipal(campos);
                    } else {
                        setCamposPrincipal([]);
                    }

                    const pendencias = mapearPendenciasFinalizadas(logs);
                    setPendenciasFinalizadas(pendencias);
                } else {
                    setCamposPrincipal([]);
                    setPendenciasFinalizadas([]);
                }
            } catch (error: any) {
                setCamposPrincipal([]);
                setPendenciasFinalizadas([]);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposPrincipal.length === 0 && pendenciasFinalizadas.length === 0;

    if (semDados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            <Separator />
            {camposPrincipal.length > 0 && (
                <div className="expanded-section">
                    <h4 className="section-title">Principal</h4>
                    <TableContainer>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="field-col">Campo</th>
                                    <th className="before-col">Antes</th>
                                    <th className="after-col">Depois</th>
                                </tr>
                            </thead>
                            <tbody>
                                {camposPrincipal.map((campo, index) => (
                                    <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                        <td className="field-cell">{campo.campo}</td>
                                        <td className="before-cell">{formatValue(campo.antes)}</td>
                                        <td className="after-cell">
                                            <div className="after-content">
                                                <span className="highlight-bar" />
                                                <span>{formatValue(campo.depois)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableContainer>
                </div>
            )}
            {pendenciasFinalizadas.length > 0 && (
                <BlocoPendenciasFinalizadas pendencias={pendenciasFinalizadas} />
            )}
        </ExpandedContent>
    );
}

